import { useCallback, useEffect, useMemo, useState } from "react";
import { getToken, isInternalEmployee, useLogin } from "../../../authConfig";
import { useMsal } from "@azure/msal-react";
import {
    FeedbackItem,
    FeedbackReason,
    getFeedbackByIdApi,
    deleteFeedbackItemApi,
    updateFeedbackCommentApi,
    updateFeedbackFailureTypeApi,
    updateFeedbackJiraTicketApi,
    updateFeedbackReviewStatusApi,
    Source
} from "../../../api";
import { Conversation } from "../../../components/conversation";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
    Title,
    Text,
    Group,
    Button,
    LoadingOverlay,
    Alert,
    Stack,
    Paper,
    Divider,
    Grid,
    Card,
    ActionIcon,
    Badge,
    Drawer,
    useMantineTheme,
    TextInput,
    Anchor,
    SegmentedControl,
    Textarea,
    Select,
    Popover,
    Table,
    Modal
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { MdArrowBack, MdRefresh, MdDeleteOutline, MdThumbDownOffAlt, MdThumbUpOffAlt, MdOpenInNew, MdSave, MdInfoOutline } from "react-icons/md";
import { TimezoneDateDisplay } from "../../../components/common/TimezoneDateDisplay";
import { SourceViewer } from "../../../components/sourceviewer";
import { FAILURE_TYPE_DEFINITIONS, FAILURE_TYPES, isFailureType } from "../../../utils/feedbackFailureTypes";

const REASON_LABELS: Record<FeedbackReason, string> = {
    incorrect_answer: "Incorrect answer",
    incomplete_answer: "Incomplete answer",
    unsuitable_asset: "Unsuitable asset"
};

export interface FeedbackDetailsProps {}

const FeedbackDetails = ({}: FeedbackDetailsProps) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const client = useLogin ? useMsal().instance : undefined;
    const [feedback, setFeedback] = useState<FeedbackItem | null>(null);
    const { feedbackId } = useParams<{ feedbackId: string }>();
    const [opened, { toggle }] = useDisclosure(false);
    const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [jiraTicketInput, setJiraTicketInput] = useState<string>("");
    const [jiraSaving, setJiraSaving] = useState(false);
    const [jiraError, setJiraError] = useState<string | null>(null);
    const [reviewStatusSaving, setReviewStatusSaving] = useState(false);
    const [failureTypeValue, setFailureTypeValue] = useState<string | null>(null);
    const [failureTypeSaving, setFailureTypeSaving] = useState(false);
    const [commentInput, setCommentInput] = useState<string>("");
    const [commentSaving, setCommentSaving] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);
    const [selectedSource, setSelectedSource] = useState<Source | undefined>(undefined);
    const [selectedSourceAnchor, setSelectedSourceAnchor] = useState<string>();
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
    const drawerWidth = useMemo(() => (isMobile ? "100%" : "80%"), [isMobile]);

    const loadFeedbackData = async () => {
        if (!feedbackId) return;

        setLoading(true);
        setError(null);
        setJiraError(null);
        setJiraSaving(false);
        setCommentError(null);
        setCommentSaving(false);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching feedback");
            }
            const feedbackData = await getFeedbackByIdApi(feedbackId, idToken);
            setFeedback(feedbackData);
            setJiraTicketInput(feedbackData.jira_ticket ?? "");
            setFailureTypeValue(feedbackData.failure_type ?? null);
            setCommentInput(feedbackData.comment ?? "");
        } catch (error) {
            console.error("Failed to fetch feedback", error);
            setError(error instanceof Error ? error.message : "Failed to load feedback details");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!feedbackId) return;

        setDeleting(true);
        setDeleteError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for deleting feedback");
            }

            await deleteFeedbackItemApi(feedbackId, idToken);
            navigate(`/administration/feedback/inbox?${searchParams.toString()}`);
        } catch (error) {
            console.error("Failed to delete feedback", error);
            setDeleteError(error instanceof Error ? error.message : "Failed to delete feedback");
        } finally {
            setDeleting(false);
        }
    };

    const saveReviewStatus = async (value: string) => {
        if (!feedbackId) return;
        setReviewStatusSaving(true);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateFeedbackReviewStatusApi(feedbackId, value === "reviewed" ? "reviewed" : null, idToken);
            setFeedback(updated);
        } catch (err) {
            console.error("Failed to save review status", err);
        } finally {
            setReviewStatusSaving(false);
        }
    };

    const saveFailureType = async (selected: string | null) => {
        if (!feedbackId) return;
        const value = selected && isFailureType(selected) ? selected : null;
        setFailureTypeValue(value);
        setFailureTypeSaving(true);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateFeedbackFailureTypeApi(feedbackId, value, idToken);
            setFeedback(updated);
        } catch (err) {
            console.error("Failed to save failure type", err);
            setFailureTypeValue(feedback?.failure_type ?? null);
        } finally {
            setFailureTypeSaving(false);
        }
    };

    const saveJiraTicket = async () => {
        if (!feedbackId) return;
        const trimmed = jiraTicketInput.trim();
        if (trimmed && !trimmed.startsWith("https://")) {
            setJiraError("URL must start with https://");
            return;
        }
        setJiraSaving(true);
        setJiraError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateFeedbackJiraTicketApi(feedbackId, jiraTicketInput.trim() || null, idToken);
            setFeedback(updated);
            setJiraTicketInput(updated.jira_ticket ?? "");
        } catch (err) {
            setJiraError(err instanceof Error ? err.message : "Failed to save JIRA ticket");
        } finally {
            setJiraSaving(false);
        }
    };

    const saveComment = async () => {
        if (!feedbackId) return;
        setCommentSaving(true);
        setCommentError(null);
        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) throw new Error("No idToken available");
            const updated = await updateFeedbackCommentApi(feedbackId, commentInput.trim() || null, idToken);
            setFeedback(updated);
            setCommentInput(updated.comment ?? "");
        } catch (err) {
            setCommentError(err instanceof Error ? err.message : "Failed to save comment");
        } finally {
            setCommentSaving(false);
        }
    };

    useEffect(() => {
        loadFeedbackData();
    }, [feedbackId, client]);

    const getFeedbackTypeIcon = (type: string) => {
        if (type?.toLowerCase() === "like") {
            return <MdThumbUpOffAlt size={18} color="white" />;
        } else {
            return <MdThumbDownOffAlt size={18} color="white" />;
        }
    };

    const getFeedbackTypeBadgeColor = (type: string) => {
        return type?.toLowerCase() === "like" ? "green" : "red";
    };

    const onSourceClick = useCallback(
        async (source: Source, anchor: string) => {
            if (source.dataSource === "playbook") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "pm360") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "orbit" && client && !(await isInternalEmployee(client))) {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "intellum") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "webpage") {
                window.open(source.url, "_blank");
            } else if (source.dataSource === "taxonomy_item") {
                window.open(source.url, "_blank");
            } else {
                toggle();
                setSelectedSource(source);
                setSelectedSourceAnchor(anchor);
            }
        },
        [client, toggle]
    );

    if (!feedbackId) {
        return (
            <Paper p="md">
                <Alert color="red" title="Error">
                    Feedback ID is required to view feedback details.
                </Alert>
            </Paper>
        );
    }

    return (
        <Stack gap="md" pos="relative">
            <LoadingOverlay visible={loading} />

            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Group>
                        <ActionIcon variant="light" onClick={() => navigate(`/administration/feedback/inbox?${searchParams.toString()}`)} size="lg">
                            <MdArrowBack size={18} />
                        </ActionIcon>
                        <div>
                            <Title order={3}>Feedback Details</Title>
                            <Text size="sm" c="dimmed">
                                {feedback?.username || "N/A"} •{" "}
                                {feedback ? <TimezoneDateDisplay timestamp={feedback.timestamp} showTime={true} showTimezone={false} size="sm" /> : ""}
                            </Text>
                        </div>
                    </Group>
                    <Group gap="xs">
                        <Button variant="light" leftSection={<MdRefresh size={16} />} onClick={loadFeedbackData} disabled={loading}>
                            Refresh
                        </Button>
                        <Button color="red" variant="light" leftSection={<MdDeleteOutline size={16} />} onClick={openDeleteModal} disabled={loading}>
                            Delete Feedback
                        </Button>
                    </Group>
                </Group>

                {error && <Alert color="red">{error}</Alert>}

                {feedback && (
                    <Paper p="md">
                        <Grid gap="lg">
                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Card withBorder radius="lg" shadow="sm">
                                    <Stack gap="sm">
                                        <Title order={5}>Feedback Details</Title>
                                        <Divider my="sm" />

                                        <Group grow align="flex-start">
                                            <Stack gap="xs">
                                                <Text fw={500}>Name</Text>
                                                <Text size="sm" c="dimmed">
                                                    {feedback.name}
                                                </Text>
                                            </Stack>

                                            <Stack gap="xs">
                                                <Text fw={500}>Username</Text>
                                                <Text size="sm" c="dimmed">
                                                    {feedback.username}
                                                </Text>
                                            </Stack>
                                        </Group>

                                        <Group grow align="flex-start">
                                            <Stack gap="xs">
                                                <Text fw={500}>Email</Text>
                                                <Text size="sm" c="dimmed">
                                                    {feedback.email || "N/A"}
                                                </Text>
                                            </Stack>

                                            <Stack gap="xs">
                                                <Text fw={500}>User ID</Text>
                                                <Text size="sm" c="dimmed">
                                                    {feedback.entra_oid || "N/A"}
                                                </Text>
                                            </Stack>
                                        </Group>

                                        <Stack gap="xs">
                                            <Text fw={500}>Date</Text>
                                            <TimezoneDateDisplay timestamp={feedback.timestamp} showTime={true} showTimezone={true} size="sm" color="dimmed" />
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>Turn</Text>
                                            <Text size="sm" c="dimmed">
                                                {feedback.turn_index !== null ? feedback.turn_index + 1 : "N/A"}
                                            </Text>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Text fw={500}>Type</Text>
                                            <Group gap="xs">
                                                <Badge
                                                    color={getFeedbackTypeBadgeColor(feedback.positive ? "like" : "dislike")}
                                                    leftSection={getFeedbackTypeIcon(feedback.positive ? "like" : "dislike")}
                                                >
                                                    {feedback.positive ? "like" : "dislike"}
                                                </Badge>
                                            </Group>
                                        </Stack>

                                        {!feedback.positive && feedback.reason && (
                                            <Stack gap="xs">
                                                <Text fw={500}>User Reason</Text>
                                                <Badge color="orange" variant="light" style={{ alignSelf: "flex-start" }}>
                                                    {REASON_LABELS[feedback.reason] ?? feedback.reason}
                                                </Badge>
                                            </Stack>
                                        )}
                                    </Stack>
                                </Card>
                            </Grid.Col>

                            <Grid.Col span={{ base: 12, md: 6 }}>
                                <Card withBorder radius="lg" shadow="sm">
                                    <Stack gap="sm">
                                        <Title order={5}>Feedback Review</Title>
                                        <Divider my="sm" />

                                        {!feedback.positive && (
                                            <Stack gap="xs">
                                                <Text fw={500}>Review Status</Text>
                                                <SegmentedControl
                                                    value={feedback.review_status ?? "not_reviewed"}
                                                    onChange={saveReviewStatus}
                                                    disabled={reviewStatusSaving}
                                                    data={[
                                                        { value: "not_reviewed", label: "Not Reviewed" },
                                                        { value: "reviewed", label: "Reviewed" }
                                                    ]}
                                                    size="sm"
                                                />
                                            </Stack>
                                        )}

                                        {!feedback.positive && (
                                            <Stack gap="xs">
                                                <Group gap="xs" align="center">
                                                    <Text fw={500}>Failure Type</Text>
                                                    <Popover width={420} position="right" withArrow shadow="md">
                                                        <Popover.Target>
                                                            <ActionIcon
                                                                variant="subtle"
                                                                size="xs"
                                                                color="gray"
                                                                style={{ cursor: "pointer" }}
                                                                aria-label="Failure type definitions"
                                                                title="Failure type definitions"
                                                            >
                                                                <MdInfoOutline size={14} />
                                                            </ActionIcon>
                                                        </Popover.Target>
                                                        <Popover.Dropdown>
                                                            <Table fz="xs" withRowBorders={false} horizontalSpacing="xs" verticalSpacing={4}>
                                                                <Table.Tbody>
                                                                    {FAILURE_TYPE_DEFINITIONS.map(({ label, description }) => (
                                                                        <Table.Tr key={label}>
                                                                            <Table.Td style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                                                                                <Text fw={600} size="xs">
                                                                                    {label}
                                                                                </Text>
                                                                            </Table.Td>
                                                                            <Table.Td>
                                                                                <Text size="xs" c="dimmed">
                                                                                    {description}
                                                                                </Text>
                                                                            </Table.Td>
                                                                        </Table.Tr>
                                                                    ))}
                                                                </Table.Tbody>
                                                            </Table>
                                                        </Popover.Dropdown>
                                                    </Popover>
                                                </Group>
                                                <Select
                                                    placeholder="Select failure type"
                                                    value={failureTypeValue}
                                                    onChange={saveFailureType}
                                                    disabled={failureTypeSaving}
                                                    clearable
                                                    size="sm"
                                                    data={[...FAILURE_TYPES]}
                                                />
                                            </Stack>
                                        )}

                                        <Stack gap="xs">
                                            <Text fw={500}>JIRA Ticket</Text>
                                            {feedback.jira_ticket && (
                                                <Anchor
                                                    href={feedback.jira_ticket}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                    size="sm"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                                                >
                                                    {feedback.jira_ticket}
                                                    <MdOpenInNew size={14} />
                                                </Anchor>
                                            )}
                                            <Group gap="xs" align="flex-end">
                                                <TextInput
                                                    style={{ flex: 1 }}
                                                    placeholder="https://jira.example.com/browse/TICKET-123"
                                                    value={jiraTicketInput}
                                                    onChange={e => setJiraTicketInput(e.currentTarget.value)}
                                                    size="sm"
                                                    error={jiraError}
                                                />
                                                <Button
                                                    size="sm"
                                                    leftSection={<MdSave size={16} />}
                                                    onClick={saveJiraTicket}
                                                    loading={jiraSaving}
                                                    disabled={jiraTicketInput === (feedback.jira_ticket ?? "")}
                                                >
                                                    Save
                                                </Button>
                                            </Group>
                                        </Stack>

                                        <Stack gap="xs">
                                            <Group justify="space-between" align="baseline">
                                                <Text fw={500}>Comment</Text>
                                                <Text size="xs" c={commentInput.length > 1000 ? "red" : "dimmed"}>
                                                    {commentInput.length}/1000
                                                </Text>
                                            </Group>
                                            <Textarea
                                                placeholder="Add an internal comment..."
                                                value={commentInput}
                                                onChange={e => setCommentInput(e.currentTarget.value.slice(0, 1000))}
                                                size="sm"
                                                autosize
                                                minRows={2}
                                                maxRows={6}
                                                error={commentError}
                                            />
                                            <Group justify="flex-end">
                                                <Button
                                                    size="sm"
                                                    leftSection={<MdSave size={16} />}
                                                    onClick={saveComment}
                                                    loading={commentSaving}
                                                    disabled={commentInput.trim() === (feedback.comment ?? "").trim()}
                                                >
                                                    Save
                                                </Button>
                                            </Group>
                                        </Stack>
                                    </Stack>
                                </Card>
                            </Grid.Col>
                        </Grid>

                        {feedback.answers && feedback.answers.length > 0 && (
                            <>
                                <Divider my="lg" />
                                <Card withBorder radius="lg" shadow="sm">
                                    <Stack gap="sm">
                                        <Title order={5}>Conversation History</Title>
                                        <Divider my="sm" />

                                        <Conversation
                                            hasError={false}
                                            showSttRequestIds
                                            isLoading={false}
                                            isStreaming={false}
                                            answers={feedback.answers}
                                            onSourceClick={onSourceClick}
                                            onFollowupQuestionClick={() => {}}
                                        />
                                    </Stack>
                                </Card>
                            </>
                        )}
                    </Paper>
                )}
            </Stack>
            <Drawer
                withOverlay={true}
                opened={opened}
                onClose={toggle}
                position="right"
                size={drawerWidth}
                title={selectedSource?.title}
                withCloseButton={true}
                transitionProps={{ transition: "fade-left", duration: 150, timingFunction: "linear" }}
                styles={{
                    body: {
                        height: "calc(100vh - 7rem)"
                    }
                }}
            >
                <SourceViewer source={selectedSource} anchor={selectedSourceAnchor} />
            </Drawer>
            <Modal opened={deleteModalOpened} onClose={deleting ? () => {} : closeDeleteModal} title="Delete Feedback" centered>
                <Stack gap="md">
                    <Text size="sm">
                        This will permanently delete this feedback item, its underlying conversation, any other feedback on that conversation, and its
                        Databricks traces. This cannot be undone.
                    </Text>
                    {deleteError && <Alert color="red">{deleteError}</Alert>}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={closeDeleteModal} disabled={deleting}>
                            Cancel
                        </Button>
                        <Button color="red" onClick={handleDelete} loading={deleting}>
                            Delete
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </Stack>
    );
};

export default FeedbackDetails;
